import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Shield, LogOut, Mail, Phone, Building2, FileText, Calendar, Trash2, Filter, Search, User, CheckCircle, Clock, AlertCircle, X, MessageSquare, Download } from 'lucide-react';
import jsPDF from 'jspdf';
import 'jspdf-autotable';

const AdminDashboard = () => {
  const [quotes, setQuotes] = useState([]);
  const [admins, setAdmins] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentAdmin, setCurrentAdmin] = useState(null);
  const [selectedQuote, setSelectedQuote] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [newNote, setNewNote] = useState('');
  const [selectedQuotes, setSelectedQuotes] = useState([]);
  const [dateFilter, setDateFilter] = useState({ start: '', end: '' });
  const [bulkAction, setBulkAction] = useState('');
  const navigate = useNavigate();

  const statusColors = {
    new: { bg: 'rgba(59, 130, 246, 0.1)', border: 'rgba(59, 130, 246, 0.3)', text: '#3B82F6' },
    assigned: { bg: 'rgba(212, 175, 55, 0.1)', border: 'rgba(212, 175, 55, 0.3)', text: '#D4AF37' },
    in_progress: { bg: 'rgba(251, 146, 60, 0.1)', border: 'rgba(251, 146, 60, 0.3)', text: '#FB923C' },
    quoted: { bg: 'rgba(34, 197, 94, 0.1)', border: 'rgba(34, 197, 94, 0.3)', text: '#22C55E' },
    closed: { bg: 'rgba(107, 114, 128, 0.1)', border: 'rgba(107, 114, 128, 0.3)', text: '#6B7280' }
  };

  useEffect(() => {
    const token = localStorage.getItem('adminToken');
    if (!token) {
      navigate('/admin/login');
      return;
    }
    fetchCurrentAdmin();
    fetchQuotes();
    fetchAdmins();
    fetchStats();
  }, [navigate, statusFilter, searchTerm]);

  const fetchCurrentAdmin = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/me`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setCurrentAdmin(data.admin);
      }
    } catch (err) {
      console.error('Failed to fetch current admin:', err);
    }
  };

  const fetchQuotes = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const params = new URLSearchParams();
      if (statusFilter !== 'all') params.append('status', statusFilter);
      if (searchTerm) params.append('search', searchTerm);
      
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/quotes?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setQuotes(data.quotes || []);
      } else if (response.status === 401) {
        localStorage.removeItem('adminToken');
        navigate('/admin/login');
      }
    } catch (err) {
      console.error('Failed to fetch quotes:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchAdmins = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/admins`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setAdmins(data.admins || []);
      }
    } catch (err) {
      console.error('Failed to fetch admins:', err);
    }
  };

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/quotes/stats/summary`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (err) {
      console.error('Failed to fetch stats:', err);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    navigate('/admin/login');
  };

  const handleStatusChange = async (quoteId, newStatus) => {
    try {
      const token = localStorage.getItem('adminToken');
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/quotes/${quoteId}/status`, {
        method: 'PATCH',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ status: newStatus })
      });
      fetchQuotes();
      fetchStats();
      if (selectedQuote && selectedQuote.id === quoteId) {
        const updated = quotes.find(q => q.id === quoteId);
        setSelectedQuote({ ...updated, status: newStatus });
      }
    } catch (err) {
      console.error('Failed to update status:', err);
    }
  };

  const handleAssign = async (quoteId, adminEmail) => {
    try {
      const token = localStorage.getItem('adminToken');
      
      if (adminEmail === null || adminEmail === '') {
        // Unassign the quote
        await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/quotes/${quoteId}/assign`, {
          method: 'PATCH',
          headers: { 
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ assigned_to: '' })
        });
      } else {
        // Assign to admin
        await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/quotes/${quoteId}/assign`, {
          method: 'PATCH',
          headers: { 
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ assigned_to: adminEmail })
        });
      }
      
      fetchQuotes();
      fetchStats();
    } catch (err) {
      console.error('Failed to assign/unassign quote:', err);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this quote request?')) return;
    
    try {
      const token = localStorage.getItem('adminToken');
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/quotes/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      fetchQuotes();
      fetchStats();
      setSelectedQuote(null);
    } catch (err) {
      console.error('Failed to delete:', err);
    }
  };

  const handleAddNote = async (quoteId) => {
    if (!newNote.trim()) return;
    
    try {
      const token = localStorage.getItem('adminToken');
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/quotes/${quoteId}/notes`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ note: newNote })
      });
      setNewNote('');
      // Refresh the selected quote
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/quotes/${quoteId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setSelectedQuote(data.quote);
      }
      fetchQuotes();
    } catch (err) {
      console.error('Failed to add note:', err);
    }
  };

  const handleDeleteNote = async (quoteId, noteId) => {
    if (!window.confirm('Delete this note?')) return;
    
    try {
      const token = localStorage.getItem('adminToken');
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/quotes/${quoteId}/notes/${noteId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      // Refresh the selected quote
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/quotes/${quoteId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setSelectedQuote(data.quote);
      }
      fetchQuotes();
    } catch (err) {
      console.error('Failed to delete note:', err);
      alert(err.response?.data?.detail || 'Failed to delete note. You can only delete your own notes.');
    }
  };

  const exportToCSV = () => {
    const csvHeaders = ['ID', 'Name', 'Email', 'Phone', 'Organization', 'Type', 'Status', 'Assigned To', 'Created At', 'Message'];
    const csvRows = quotes.map(quote => [
      quote.id,
      quote.contact_info.fullName,
      quote.contact_info.email,
      quote.contact_info.phone,
      quote.contact_info.organization,
      quote.contact_info.organizationType,
      quote.status,
      quote.assigned_to || 'Unassigned',
      new Date(quote.created_at).toLocaleString(),
      `"${quote.contact_info.message.replace(/"/g, '""')}"`
    ]);

    const csvContent = [
      csvHeaders.join(','),
      ...csvRows.map(row => row.join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `wolf-eye-quotes-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const exportQuoteToPDF = (quote) => {
    const doc = new jsPDF();
    
    // Add Labana branding
    doc.setFillColor(0, 0, 0);
    doc.rect(0, 0, 210, 40, 'F');
    
    doc.setTextColor(212, 175, 55);
    doc.setFontSize(24);
    doc.setFont(undefined, 'bold');
    doc.text('LABANA DYNAMICS OU', 105, 15, { align: 'center' });
    
    doc.setFontSize(12);
    doc.setFont(undefined, 'normal');
    doc.text('Wolf Eye Defense System - Quote Request', 105, 25, { align: 'center' });
    
    doc.setFontSize(10);
    doc.text(`Quote ID: ${quote.id}`, 105, 32, { align: 'center' });
    
    // Reset colors
    doc.setTextColor(0, 0, 0);
    let yPos = 50;
    
    // Contact Information
    doc.setFontSize(14);
    doc.setFont(undefined, 'bold');
    doc.setTextColor(212, 175, 55);
    doc.text('Contact Information', 20, yPos);
    yPos += 10;
    
    doc.setFontSize(10);
    doc.setFont(undefined, 'normal');
    doc.setTextColor(0, 0, 0);
    doc.text(`Name: ${quote.contact_info.fullName}`, 20, yPos);
    yPos += 6;
    doc.text(`Email: ${quote.contact_info.email}`, 20, yPos);
    yPos += 6;
    doc.text(`Phone: ${quote.contact_info.phone}`, 20, yPos);
    yPos += 6;
    doc.text(`Organization: ${quote.contact_info.organization}`, 20, yPos);
    yPos += 6;
    doc.text(`Type: ${quote.contact_info.organizationType}`, 20, yPos);
    yPos += 10;
    
    // Territory Data
    if (quote.territory_data && quote.territory_data.recommended) {
      doc.setFontSize(14);
      doc.setFont(undefined, 'bold');
      doc.setTextColor(212, 175, 55);
      doc.text('System Configuration', 20, yPos);
      yPos += 10;
      
      const { recommended, deploymentType, totalCost, droneConfig } = quote.territory_data;
      
      doc.setFontSize(10);
      doc.setFont(undefined, 'normal');
      doc.setTextColor(0, 0, 0);
      
      if (deploymentType) {
        doc.text(`Deployment Type: ${deploymentType}`, 20, yPos);
        yPos += 6;
      }
      
      doc.text(`Modules Required: ${recommended.modulesNeeded?.toLocaleString() || 'N/A'}`, 20, yPos);
      yPos += 6;
      doc.text(`Total Drones: ${recommended.totalDrones?.toLocaleString() || 'N/A'}`, 20, yPos);
      yPos += 6;
      doc.text(`Radar Units: ${recommended.radarUnits || 'N/A'}`, 20, yPos);
      yPos += 6;
      doc.text(`Coverage Area: ${recommended.totalKm2 || 'N/A'} km²`, 20, yPos);
      yPos += 6;
      
      if (totalCost) {
        doc.setFont(undefined, 'bold');
        doc.text(`Total Cost: ${formatPrice(totalCost)}`, 20, yPos);
        doc.setFont(undefined, 'normal');
        yPos += 10;
      }
      
      if (droneConfig) {
        doc.setFontSize(12);
        doc.setFont(undefined, 'bold');
        doc.setTextColor(212, 175, 55);
        doc.text('Drone Distribution', 20, yPos);
        yPos += 8;
        
        doc.setFontSize(10);
        doc.setFont(undefined, 'normal');
        doc.setTextColor(0, 0, 0);
        doc.text(`Anti-Drone: ${droneConfig.antiDrone?.toLocaleString() || 0}`, 20, yPos);
        yPos += 6;
        doc.text(`Anti-Tank: ${droneConfig.antiTank?.toLocaleString() || 0}`, 20, yPos);
        yPos += 6;
        doc.text(`Anti-Personnel: ${droneConfig.antiPersonnel?.toLocaleString() || 0}`, 20, yPos);
        yPos += 10;
      }
    }
    
    // Message
    doc.setFontSize(14);
    doc.setFont(undefined, 'bold');
    doc.setTextColor(212, 175, 55);
    doc.text('Message', 20, yPos);
    yPos += 8;
    
    doc.setFontSize(10);
    doc.setFont(undefined, 'normal');
    doc.setTextColor(0, 0, 0);
    const messageLines = doc.splitTextToSize(quote.contact_info.message, 170);
    doc.text(messageLines, 20, yPos);
    yPos += messageLines.length * 6 + 10;
    
    // Notes
    if (quote.notes && quote.notes.length > 0) {
      doc.setFontSize(14);
      doc.setFont(undefined, 'bold');
      doc.setTextColor(212, 175, 55);
      doc.text('Internal Notes', 20, yPos);
      yPos += 8;
      
      doc.setFontSize(9);
      quote.notes.forEach(note => {
        doc.setTextColor(100, 100, 100);
        doc.text(`${note.created_by} - ${new Date(note.created_at).toLocaleString()}`, 20, yPos);
        yPos += 5;
        doc.setTextColor(0, 0, 0);
        const noteLines = doc.splitTextToSize(note.text, 170);
        doc.text(noteLines, 20, yPos);
        yPos += noteLines.length * 5 + 5;
      });
    }
    
    // Footer
    doc.setFillColor(0, 0, 0);
    doc.rect(0, 277, 210, 20, 'F');
    doc.setTextColor(212, 175, 55);
    doc.setFontSize(8);
    doc.text('LABANA DYNAMICS OU - Confidential Quote', 105, 285, { align: 'center' });
    doc.text(`Generated: ${new Date().toLocaleString()}`, 105, 290, { align: 'center' });
    
    doc.save(`wolf-eye-quote-${quote.id}.pdf`);
  };

  const handleBulkAction = async () => {
    if (selectedQuotes.length === 0 || !bulkAction) return;
    
    const token = localStorage.getItem('adminToken');
    
    if (bulkAction === 'delete') {
      if (!window.confirm(`Delete ${selectedQuotes.length} quote(s)?`)) return;
      
      for (const quoteId of selectedQuotes) {
        await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/quotes/${quoteId}`, {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${token}` }
        });
      }
    } else if (bulkAction.startsWith('status:')) {
      const status = bulkAction.replace('status:', '');
      for (const quoteId of selectedQuotes) {
        await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/quotes/${quoteId}/status`, {
          method: 'PATCH',
          headers: { 
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ status })
        });
      }
    } else if (bulkAction.startsWith('assign:')) {
      const adminEmail = bulkAction.replace('assign:', '');
      for (const quoteId of selectedQuotes) {
        await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/quotes/${quoteId}/assign`, {
          method: 'PATCH',
          headers: { 
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ assigned_to: adminEmail })
        });
      }
    }
    
    setSelectedQuotes([]);
    setBulkAction('');
    fetchQuotes();
    fetchStats();
  };

  const toggleSelectQuote = (quoteId) => {
    setSelectedQuotes(prev => 
      prev.includes(quoteId) 
        ? prev.filter(id => id !== quoteId)
        : [...prev, quoteId]
    );
  };

  const toggleSelectAll = () => {
    if (selectedQuotes.length === quotes.length) {
      setSelectedQuotes([]);
    } else {
      setSelectedQuotes(quotes.map(q => q.id));
    }
  };

  const filteredQuotesByDate = () => {
    if (!dateFilter.start && !dateFilter.end) return quotes;
    
    return quotes.filter(quote => {
      const quoteDate = new Date(quote.created_at);
      const start = dateFilter.start ? new Date(dateFilter.start) : null;
      const end = dateFilter.end ? new Date(dateFilter.end) : null;
      
      if (start && end) return quoteDate >= start && quoteDate <= end;
      if (start) return quoteDate >= start;
      if (end) return quoteDate <= end;
      return true;
    });
  };

  const formatPrice = (price) => {
    if (price >= 1000000) return `€${(price / 1000000).toFixed(2)}M`;
    if (price >= 1000) return `€${Math.round(price / 1000)}K`;
    return `€${price}`;
  };

  const renderTerritoryData = (territoryData) => {
    if (!territoryData || Object.keys(territoryData).length === 0) {
      return <div style={{ color: 'rgba(255, 255, 255, 0.5)', fontSize: '13px' }}>No territory data provided</div>;
    }

    const { layers, recommended, droneConfig, reserves, environment, totalCost, deploymentType } = territoryData;

    return (
      <div>
        {/* Deployment Summary */}
        {deploymentType && (
          <div style={{ 
            padding: '16px', 
            background: 'rgba(212, 175, 55, 0.1)', 
            border: '1px solid rgba(212, 175, 55, 0.3)',
            marginBottom: '16px'
          }}>
            <div style={{ fontSize: '11px', color: '#D4AF37', fontWeight: '700', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '1px' }}>
              Deployment Type
            </div>
            <div style={{ fontSize: '18px', fontWeight: '700', color: '#D4AF37' }}>{deploymentType}</div>
            {totalCost && (
              <div style={{ fontSize: '24px', fontWeight: '700', color: '#FFFFFF', marginTop: '8px' }}>
                {formatPrice(totalCost)}
              </div>
            )}
          </div>
        )}

        {/* System Overview */}
        {recommended && (
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(2, 1fr)', 
            gap: '12px',
            marginBottom: '16px'
          }}>
            <div style={{ padding: '12px', background: 'rgba(59, 130, 246, 0.1)', border: '1px solid rgba(59, 130, 246, 0.3)' }}>
              <div style={{ fontSize: '10px', color: '#3B82F6', marginBottom: '4px', textTransform: 'uppercase' }}>Modules</div>
              <div style={{ fontSize: '20px', fontWeight: '700', color: '#3B82F6' }}>{recommended.modulesNeeded?.toLocaleString()}</div>
            </div>
            <div style={{ padding: '12px', background: 'rgba(212, 175, 55, 0.1)', border: '1px solid rgba(212, 175, 55, 0.3)' }}>
              <div style={{ fontSize: '10px', color: '#D4AF37', marginBottom: '4px', textTransform: 'uppercase' }}>Total Drones</div>
              <div style={{ fontSize: '20px', fontWeight: '700', color: '#D4AF37' }}>{recommended.totalDrones?.toLocaleString()}</div>
            </div>
            <div style={{ padding: '12px', background: 'rgba(251, 146, 60, 0.1)', border: '1px solid rgba(251, 146, 60, 0.3)' }}>
              <div style={{ fontSize: '10px', color: '#FB923C', marginBottom: '4px', textTransform: 'uppercase' }}>Radar Units</div>
              <div style={{ fontSize: '20px', fontWeight: '700', color: '#FB923C' }}>{recommended.radarUnits}</div>
            </div>
            <div style={{ padding: '12px', background: 'rgba(34, 197, 94, 0.1)', border: '1px solid rgba(34, 197, 94, 0.3)' }}>
              <div style={{ fontSize: '10px', color: '#22C55E', marginBottom: '4px', textTransform: 'uppercase' }}>Coverage</div>
              <div style={{ fontSize: '20px', fontWeight: '700', color: '#22C55E' }}>{recommended.totalKm2} km²</div>
            </div>
          </div>
        )}

        {/* Defense Layers */}
        {layers && layers.length > 0 && (
          <div style={{ marginBottom: '16px' }}>
            <div style={{ fontSize: '11px', color: '#D4AF37', fontWeight: '700', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '1px' }}>
              Defense Layers ({layers.length})
            </div>
            {layers.map((layer, index) => (
              <div key={index} style={{ 
                padding: '12px', 
                background: 'rgba(0, 0, 0, 0.3)', 
                border: '1px solid rgba(212, 175, 55, 0.2)',
                marginBottom: '8px',
                fontSize: '12px'
              }}>
                <div style={{ color: '#D4AF37', fontWeight: '600', marginBottom: '6px' }}>
                  {layer.name || `Layer ${index + 1}`}: {layer.type}
                </div>
                
                {/* CITY type */}
                {layer.type === 'CITY' && (
                  <div style={{ color: 'rgba(255, 255, 255, 0.85)' }}>
                    <strong>Territory:</strong> {layer.territoryInput} {layer.metric === 'km2' ? 'km²' : 'm²'}
                  </div>
                )}
                
                {/* LINE type */}
                {layer.type === 'LINE' && (
                  <div style={{ color: 'rgba(255, 255, 255, 0.85)' }}>
                    <strong>Perimeter:</strong> {layer.territoryInput} km
                  </div>
                )}
                
                {/* INFRASTRUCTURE type */}
                {layer.type === 'INFRASTRUCTURE' && layer.infrastructures && (
                  <div style={{ color: 'rgba(255, 255, 255, 0.85)' }}>
                    <strong>Infrastructure Sites:</strong> {layer.infrastructures.length} site(s)
                    <div style={{ marginLeft: '12px', marginTop: '6px' }}>
                      {layer.infrastructures.map((infra, infraIndex) => (
                        <div key={infra.id || infraIndex} style={{ 
                          marginBottom: '4px',
                          padding: '6px',
                          background: 'rgba(59, 130, 246, 0.1)',
                          border: '1px solid rgba(59, 130, 246, 0.2)'
                        }}>
                          Site {infraIndex + 1}: {infra.area ? `${infra.area} ${infra.metric === 'km2' ? 'km²' : 'm²'}` : 'Not specified'}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Distance from previous layer */}
                {index > 0 && layer.distanceFromPrevious && (
                  <div style={{ color: '#FB923C', marginTop: '6px' }}>
                    <strong>Distance from Layer {index}:</strong> {layer.distanceFromPrevious} km
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Drone Configuration */}
        {droneConfig && (
          <div style={{ marginBottom: '16px' }}>
            <div style={{ fontSize: '11px', color: '#D4AF37', fontWeight: '700', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '1px' }}>
              Drone Distribution
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '8px', fontSize: '12px' }}>
              <div style={{ padding: '8px', background: 'rgba(59, 130, 246, 0.1)', border: '1px solid rgba(59, 130, 246, 0.2)', textAlign: 'center' }}>
                <div style={{ color: '#3B82F6', fontWeight: '600' }}>{droneConfig.antiDrone?.toLocaleString()}</div>
                <div style={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: '10px' }}>Anti-Drone</div>
              </div>
              <div style={{ padding: '8px', background: 'rgba(251, 146, 60, 0.1)', border: '1px solid rgba(251, 146, 60, 0.2)', textAlign: 'center' }}>
                <div style={{ color: '#FB923C', fontWeight: '600' }}>{droneConfig.antiTank?.toLocaleString()}</div>
                <div style={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: '10px' }}>Anti-Tank</div>
              </div>
              <div style={{ padding: '8px', background: 'rgba(212, 175, 55, 0.1)', border: '1px solid rgba(212, 175, 55, 0.2)', textAlign: 'center' }}>
                <div style={{ color: '#D4AF37', fontWeight: '600' }}>{droneConfig.antiPersonnel?.toLocaleString()}</div>
                <div style={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: '10px' }}>Anti-Personnel</div>
              </div>
            </div>
          </div>
        )}

        {/* Environment */}
        {environment && (
          <div style={{ marginBottom: '16px' }}>
            <div style={{ fontSize: '11px', color: '#D4AF37', fontWeight: '700', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '1px' }}>
              Operational Environment
            </div>
            <div style={{ fontSize: '12px', color: 'rgba(255, 255, 255, 0.85)', lineHeight: '1.8' }}>
              {environment.country && <div><strong>Country:</strong> {environment.country}</div>}
              {environment.region && <div><strong>Region:</strong> {environment.region}</div>}
              
              {/* Critical Zone */}
              {environment.isCriticalZone && (
                <div style={{ color: '#FF6B6B', marginTop: '8px', padding: '8px', background: 'rgba(255, 107, 107, 0.1)', border: '1px solid rgba(255, 107, 107, 0.3)' }}>
                  ⚠ Critical Defense Zone
                </div>
              )}
              
              {/* Power Infrastructure */}
              <div style={{ marginTop: '8px' }}>
                <strong>Power Infrastructure:</strong>
                <div style={{ marginLeft: '12px', marginTop: '4px' }}>
                  {environment.hasCentralPower ? (
                    <div style={{ color: '#22C55E' }}>✓ Connected to Central Power Grid</div>
                  ) : (
                    <div style={{ color: '#FF6B6B' }}>✗ No Central Power Connection</div>
                  )}
                  {environment.needsIndependentPower && (
                    <div style={{ color: '#FB923C' }}>⚡ Requires Independent Power Supply</div>
                  )}
                </div>
              </div>
              
              {/* Camouflage */}
              {environment.needsCamouflage && (
                <div style={{ marginTop: '8px', color: '#D4AF37' }}>
                  🎯 Camouflage Required
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div style={{ minHeight: '100vh', background: '#000000' }}>
      {/* Header */}
      <header style={{
        background: 'rgba(0, 0, 0, 0.95)',
        borderBottom: '1px solid rgba(212, 175, 55, 0.2)',
        padding: '20px 40px',
        position: 'sticky',
        top: 0,
        zIndex: 100
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', maxWidth: '1600px', margin: '0 auto' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
            <img 
              src="https://customer-assets.emergentagent.com/job_wolf-eye/artifacts/128p77ad_LABANA%20Gold%20NO%20TEXT%2C%20TRANSPARENT%20-%20Copy.png" 
              alt="Labana Dynamics" 
              style={{ width: '48px', height: '48px' }}
            />
            <div style={{ borderLeft: '1px solid rgba(212, 175, 55, 0.3)', paddingLeft: '20px' }}>
              <h1 style={{ fontSize: '24px', fontWeight: '700', color: '#FFFFFF', margin: 0 }}>Admin CMS</h1>
              <p style={{ fontSize: '12px', color: 'rgba(255, 255, 255, 0.6)', margin: 0 }}>
                {currentAdmin ? `${currentAdmin.name} (${currentAdmin.role})` : 'Loading...'}
              </p>
            </div>
          </div>
          <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
            <a
              href="/"
              style={{
                padding: '10px 20px',
                background: 'rgba(212, 175, 55, 0.1)',
                border: '1px solid rgba(212, 175, 55, 0.3)',
                color: '#D4AF37',
                fontSize: '14px',
                fontWeight: '600',
                textDecoration: 'none',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}
            >
              ← Back to Website
            </a>
            <button
              onClick={handleLogout}
              style={{
                padding: '10px 20px',
                background: 'rgba(255, 0, 0, 0.1)',
                border: '1px solid rgba(255, 0, 0, 0.3)',
                color: '#FF6B6B',
                fontSize: '14px',
                fontWeight: '600',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}
            >
              <LogOut size={16} />
              Logout
            </button>
          </div>
        </div>
      </header>

      {/* Stats Bar */}
      {stats && (
        <div style={{ background: 'rgba(212, 175, 55, 0.05)', borderBottom: '1px solid rgba(212, 175, 55, 0.1)', padding: '20px 40px' }}>
          <div style={{ maxWidth: '1600px', margin: '0 auto', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '16px' }}>
            {Object.entries(stats.by_status).map(([status, count]) => (
              <div key={status} style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '28px', fontWeight: '700', color: statusColors[status].text, marginBottom: '4px' }}>
                  {count}
                </div>
                <div style={{ fontSize: '11px', fontWeight: '600', color: 'rgba(255, 255, 255, 0.6)', textTransform: 'uppercase', letterSpacing: '1px' }}>
                  {status.replace('_', ' ')}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Main Content */}
      <div style={{ maxWidth: '1600px', margin: '0 auto', padding: '40px', display: 'grid', gridTemplateColumns: selectedQuote ? '2fr 1fr' : '1fr', gap: '24px' }}>
        {/* Quote List */}
        <div>
          {/* Filters */}
          <div style={{ marginBottom: '24px' }}>
            <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', alignItems: 'center', marginBottom: '12px' }}>
              <div style={{ flex: '1', minWidth: '250px', position: 'relative' }}>
                <Search size={18} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#666' }} />
                <input
                  type="text"
                  placeholder="Search by name, email, organization..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '12px 12px 12px 40px',
                    background: 'rgba(0, 0, 0, 0.5)',
                    border: '1px solid rgba(212, 175, 55, 0.2)',
                    color: '#FFFFFF',
                    fontSize: '14px'
                  }}
                />
              </div>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                style={{
                  padding: '12px 16px',
                  background: 'rgba(0, 0, 0, 0.5)',
                  border: '1px solid rgba(212, 175, 55, 0.2)',
                  color: '#FFFFFF',
                  fontSize: '14px',
                  cursor: 'pointer'
                }}
              >
                <option value="all">All Status</option>
                <option value="new">New</option>
                <option value="assigned">Assigned</option>
                <option value="in_progress">In Progress</option>
                <option value="quoted">Quoted</option>
                <option value="closed">Closed</option>
              </select>
              <button
                onClick={exportToCSV}
                style={{
                  padding: '12px 20px',
                  background: 'rgba(34, 197, 94, 0.1)',
                  border: '1px solid rgba(34, 197, 94, 0.3)',
                  color: '#22C55E',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  whiteSpace: 'nowrap'
                }}
              >
                📊 Export CSV
              </button>
            </div>

            {/* Date Range Filter */}
            <div style={{ display: 'flex', gap: '12px', alignItems: 'center', marginBottom: '12px' }}>
              <span style={{ fontSize: '13px', color: 'rgba(255, 255, 255, 0.7)', whiteSpace: 'nowrap' }}>Date Range:</span>
              <input
                type="date"
                value={dateFilter.start}
                onChange={(e) => setDateFilter({...dateFilter, start: e.target.value})}
                style={{
                  padding: '10px',
                  background: 'rgba(0, 0, 0, 0.5)',
                  border: '1px solid rgba(212, 175, 55, 0.2)',
                  color: '#FFFFFF',
                  fontSize: '13px'
                }}
              />
              <span style={{ color: 'rgba(255, 255, 255, 0.5)' }}>to</span>
              <input
                type="date"
                value={dateFilter.end}
                onChange={(e) => setDateFilter({...dateFilter, end: e.target.value})}
                style={{
                  padding: '10px',
                  background: 'rgba(0, 0, 0, 0.5)',
                  border: '1px solid rgba(212, 175, 55, 0.2)',
                  color: '#FFFFFF',
                  fontSize: '13px'
                }}
              />
              {(dateFilter.start || dateFilter.end) && (
                <button
                  onClick={() => setDateFilter({ start: '', end: '' })}
                  style={{
                    padding: '10px 16px',
                    background: 'rgba(255, 0, 0, 0.1)',
                    border: '1px solid rgba(255, 0, 0, 0.2)',
                    color: '#FF6B6B',
                    fontSize: '13px',
                    cursor: 'pointer'
                  }}
                >
                  Clear
                </button>
              )}
            </div>

            {/* Bulk Actions */}
            {selectedQuotes.length > 0 && (
              <div style={{ 
                display: 'flex', 
                gap: '12px', 
                alignItems: 'center',
                padding: '12px',
                background: 'rgba(212, 175, 55, 0.1)',
                border: '1px solid rgba(212, 175, 55, 0.3)'
              }}>
                <span style={{ fontSize: '13px', color: '#D4AF37', fontWeight: '600' }}>
                  {selectedQuotes.length} selected
                </span>
                <select
                  value={bulkAction}
                  onChange={(e) => setBulkAction(e.target.value)}
                  style={{
                    padding: '8px 12px',
                    background: '#000',
                    border: '1px solid rgba(212, 175, 55, 0.3)',
                    color: '#FFF',
                    fontSize: '13px'
                  }}
                >
                  <option value="">Choose Action...</option>
                  <optgroup label="Change Status">
                    <option value="status:assigned">Mark as Assigned</option>
                    <option value="status:in_progress">Mark as In Progress</option>
                    <option value="status:quoted">Mark as Quoted</option>
                    <option value="status:closed">Mark as Closed</option>
                  </optgroup>
                  <optgroup label="Assign To">
                    {admins.map(admin => (
                      <option key={admin.id} value={`assign:${admin.email}`}>Assign to {admin.name}</option>
                    ))}
                  </optgroup>
                  <option value="delete">Delete Selected</option>
                </select>
                <button
                  onClick={handleBulkAction}
                  disabled={!bulkAction}
                  style={{
                    padding: '8px 16px',
                    background: bulkAction ? 'rgba(212, 175, 55, 0.2)' : 'rgba(100, 100, 100, 0.1)',
                    border: `1px solid ${bulkAction ? 'rgba(212, 175, 55, 0.4)' : 'rgba(100, 100, 100, 0.2)'}`,
                    color: bulkAction ? '#D4AF37' : '#666',
                    fontSize: '13px',
                    fontWeight: '600',
                    cursor: bulkAction ? 'pointer' : 'not-allowed'
                  }}
                >
                  Apply
                </button>
                <button
                  onClick={() => setSelectedQuotes([])}
                  style={{
                    padding: '8px 16px',
                    background: 'rgba(255, 0, 0, 0.1)',
                    border: '1px solid rgba(255, 0, 0, 0.2)',
                    color: '#FF6B6B',
                    fontSize: '13px',
                    cursor: 'pointer'
                  }}
                >
                  Clear
                </button>
              </div>
            )}
          </div>

          {/* Quote Cards */}
          {loading ? (
            <div style={{ textAlign: 'center', padding: '80px 20px', color: 'rgba(255, 255, 255, 0.6)' }}>
              Loading...
            </div>
          ) : quotes.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '80px 20px', color: 'rgba(255, 255, 255, 0.6)' }}>
              No quote requests found
            </div>
          ) : (
            <div style={{ display: 'grid', gap: '16px' }}>
              {/* Select All Checkbox */}
              {filteredQuotesByDate().length > 0 && (
                <div style={{ 
                  padding: '12px',
                  background: 'rgba(212, 175, 55, 0.05)',
                  border: '1px solid rgba(212, 175, 55, 0.2)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  cursor: 'pointer'
                }}
                onClick={toggleSelectAll}
                >
                  <input
                    type="checkbox"
                    checked={selectedQuotes.length === filteredQuotesByDate().length}
                    onChange={toggleSelectAll}
                    style={{ cursor: 'pointer' }}
                  />
                  <span style={{ fontSize: '13px', color: 'rgba(255, 255, 255, 0.85)', fontWeight: '600' }}>
                    Select All ({filteredQuotesByDate().length})
                  </span>
                </div>
              )}

              {filteredQuotesByDate().map((quote) => (
                <div 
                  key={quote.id}
                  style={{
                    background: selectedQuote?.id === quote.id ? 'rgba(212, 175, 55, 0.1)' : 'rgba(0, 0, 0, 0.5)',
                    border: `1px solid ${selectedQuote?.id === quote.id ? 'rgba(212, 175, 55, 0.4)' : 'rgba(212, 175, 55, 0.2)'}`,
                    padding: '24px',
                    transition: 'all 0.2s',
                    display: 'flex',
                    gap: '16px'
                  }}
                >
                  {/* Checkbox */}
                  <div style={{ paddingTop: '4px' }}>
                    <input
                      type="checkbox"
                      checked={selectedQuotes.includes(quote.id)}
                      onChange={(e) => {
                        e.stopPropagation();
                        toggleSelectQuote(quote.id);
                      }}
                      style={{ cursor: 'pointer', width: '18px', height: '18px' }}
                    />
                  </div>

                  {/* Quote Content */}
                  <div 
                    style={{ flex: 1, cursor: 'pointer' }}
                    onClick={() => setSelectedQuote(quote)}
                  >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px' }}>
                    <div>
                      <div style={{
                        display: 'inline-block',
                        padding: '4px 10px',
                        background: statusColors[quote.status].bg,
                        border: `1px solid ${statusColors[quote.status].border}`,
                        color: statusColors[quote.status].text,
                        fontSize: '10px',
                        fontWeight: '700',
                        letterSpacing: '1px',
                        textTransform: 'uppercase',
                        marginBottom: '8px'
                      }}>
                        {quote.status.replace('_', ' ')}
                      </div>
                      <h3 style={{ fontSize: '18px', fontWeight: '700', color: '#FFFFFF', marginBottom: '4px' }}>
                        {quote.contact_info.fullName}
                      </h3>
                      <p style={{ fontSize: '12px', color: 'rgba(255, 255, 255, 0.6)' }}>
                        <Calendar size={12} style={{ display: 'inline', marginRight: '6px', verticalAlign: 'middle' }} />
                        {new Date(quote.created_at).toLocaleString()}
                      </p>
                    </div>
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', fontSize: '13px' }}>
                    <div>
                      <span style={{ color: '#666' }}>Email: </span>
                      <span style={{ color: '#FFF' }}>{quote.contact_info.email}</span>
                    </div>
                    <div>
                      <span style={{ color: '#666' }}>Org: </span>
                      <span style={{ color: '#FFF' }}>{quote.contact_info.organization}</span>
                    </div>
                  </div>

                    {quote.assigned_to && (
                      <div style={{ marginTop: '12px', fontSize: '12px', color: '#D4AF37' }}>
                        <User size={12} style={{ display: 'inline', marginRight: '6px', verticalAlign: 'middle' }} />
                        Assigned to: {quote.assigned_to}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Quote Detail Panel */}
        {selectedQuote && (
          <div style={{ position: 'sticky', top: '120px', height: 'fit-content' }}>
            <div style={{
              background: 'rgba(0, 0, 0, 0.8)',
              border: '1px solid rgba(212, 175, 55, 0.3)',
              padding: '24px',
              maxHeight: 'calc(100vh - 180px)',
              overflowY: 'auto'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                <h3 style={{ fontSize: '18px', fontWeight: '700', color: '#D4AF37', margin: 0 }}>Quote Details</h3>
                <button onClick={() => setSelectedQuote(null)} style={{ background: 'none', border: 'none', color: '#666', cursor: 'pointer' }}>
                  <X size={20} />
                </button>
              </div>

              {/* Contact Info */}
              <div style={{ marginBottom: '20px', padding: '16px', background: 'rgba(212, 175, 55, 0.05)', border: '1px solid rgba(212, 175, 55, 0.1)' }}>
                <div style={{ fontSize: '11px', color: '#D4AF37', fontWeight: '700', marginBottom: '12px', textTransform: 'uppercase', letterSpacing: '1px' }}>Contact Information</div>
                <div style={{ fontSize: '13px', lineHeight: '1.8', color: '#FFF' }}>
                  <div><strong>Name:</strong> {selectedQuote.contact_info.fullName}</div>
                  <div><strong>Email:</strong> {selectedQuote.contact_info.email}</div>
                  <div><strong>Phone:</strong> {selectedQuote.contact_info.phone}</div>
                  <div><strong>Organization:</strong> {selectedQuote.contact_info.organization}</div>
                  <div><strong>Type:</strong> {selectedQuote.contact_info.organizationType}</div>
                  {selectedQuote.contact_info.country && <div><strong>Country:</strong> {selectedQuote.contact_info.country}</div>}
                </div>
              </div>

              {/* Territory Data */}
              {selectedQuote.territory_data && Object.keys(selectedQuote.territory_data).length > 0 && (
                <div style={{ marginBottom: '20px', padding: '16px', background: 'rgba(59, 130, 246, 0.05)', border: '1px solid rgba(59, 130, 246, 0.1)' }}>
                  <div style={{ fontSize: '11px', color: '#3B82F6', fontWeight: '700', marginBottom: '12px', textTransform: 'uppercase', letterSpacing: '1px' }}>
                    Territory Configuration
                  </div>
                  {renderTerritoryData(selectedQuote.territory_data)}
                </div>
              )}

              {/* Message */}
              <div style={{ marginBottom: '20px', padding: '16px', background: 'rgba(0, 0, 0, 0.5)', border: '1px solid rgba(212, 175, 55, 0.1)' }}>
                <div style={{ fontSize: '11px', color: '#D4AF37', fontWeight: '700', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '1px' }}>Message</div>
                <div style={{ fontSize: '13px', color: 'rgba(255, 255, 255, 0.85)', lineHeight: '1.6' }}>
                  {selectedQuote.contact_info.message}
                </div>
              </div>

              {/* Status Management */}
              <div style={{ marginBottom: '20px' }}>
                <div style={{ fontSize: '11px', color: '#D4AF37', fontWeight: '700', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '1px' }}>Change Status</div>
                <select
                  value={selectedQuote.status}
                  onChange={(e) => handleStatusChange(selectedQuote.id, e.target.value)}
                  style={{
                    width: '100%',
                    padding: '10px',
                    background: '#000',
                    border: '1px solid rgba(212, 175, 55, 0.3)',
                    color: '#FFF',
                    fontSize: '13px'
                  }}
                >
                  <option value="new">New</option>
                  <option value="assigned">Assigned</option>
                  <option value="in_progress">In Progress</option>
                  <option value="quoted">Quoted</option>
                  <option value="closed">Closed</option>
                </select>
              </div>

              {/* Assignment */}
              <div style={{ marginBottom: '20px' }}>
                <div style={{ fontSize: '11px', color: '#D4AF37', fontWeight: '700', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '1px' }}>Assign To</div>
                <select
                  value={selectedQuote.assigned_to || ''}
                  onChange={(e) => {
                    if (e.target.value === '') {
                      // Unassign
                      handleAssign(selectedQuote.id, null);
                    } else {
                      handleAssign(selectedQuote.id, e.target.value);
                    }
                  }}
                  disabled={currentAdmin?.role === 'analyst'}
                  style={{
                    width: '100%',
                    padding: '10px',
                    background: currentAdmin?.role === 'analyst' ? '#222' : '#000',
                    border: '1px solid rgba(212, 175, 55, 0.3)',
                    color: currentAdmin?.role === 'analyst' ? '#666' : '#FFF',
                    fontSize: '13px',
                    cursor: currentAdmin?.role === 'analyst' ? 'not-allowed' : 'pointer'
                  }}
                >
                  <option value="">⊘ Unassigned</option>
                  {admins.map(admin => (
                    <option key={admin.id} value={admin.email}>{admin.name} ({admin.email})</option>
                  ))}
                </select>
                {currentAdmin?.role === 'analyst' && (
                  <div style={{ fontSize: '11px', color: '#FF6B6B', marginTop: '4px' }}>
                    ⚠ Analysts cannot assign quotes
                  </div>
                )}
              </div>

              {/* Notes Section */}
              <div style={{ marginBottom: '20px' }}>
                <div style={{ fontSize: '11px', color: '#D4AF37', fontWeight: '700', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '1px' }}>
                  <MessageSquare size={12} style={{ display: 'inline', marginRight: '6px', verticalAlign: 'middle' }} />
                  Internal Notes
                </div>
                
                {/* Existing Notes */}
                {selectedQuote.notes && selectedQuote.notes.length > 0 && (
                  <div style={{ marginBottom: '12px', maxHeight: '200px', overflowY: 'auto' }}>
                    {selectedQuote.notes.map((note) => (
                      <div key={note.id} style={{
                        padding: '12px',
                        background: 'rgba(212, 175, 55, 0.05)',
                        border: '1px solid rgba(212, 175, 55, 0.1)',
                        marginBottom: '8px',
                        fontSize: '12px',
                        position: 'relative'
                      }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                          <div style={{ flex: 1 }}>
                            <div style={{ color: '#D4AF37', marginBottom: '4px', fontSize: '11px' }}>
                              {note.created_by} • {new Date(note.created_at).toLocaleString()}
                            </div>
                            <div style={{ color: 'rgba(255, 255, 255, 0.85)' }}>{note.text}</div>
                          </div>
                          {currentAdmin && note.created_by === currentAdmin.email && (
                            <button
                              onClick={() => handleDeleteNote(selectedQuote.id, note.id)}
                              style={{
                                background: 'rgba(255, 0, 0, 0.1)',
                                border: '1px solid rgba(255, 0, 0, 0.3)',
                                color: '#FF6B6B',
                                padding: '4px 8px',
                                fontSize: '10px',
                                cursor: 'pointer',
                                marginLeft: '8px'
                              }}
                            >
                              Delete
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Add Note */}
                <div style={{ display: 'flex', gap: '8px' }}>
                  <input
                    type="text"
                    value={newNote}
                    onChange={(e) => setNewNote(e.target.value)}
                    placeholder="Add a note..."
                    onKeyPress={(e) => e.key === 'Enter' && handleAddNote(selectedQuote.id)}
                    style={{
                      flex: 1,
                      padding: '10px',
                      background: '#000',
                      border: '1px solid rgba(212, 175, 55, 0.3)',
                      color: '#FFF',
                      fontSize: '13px'
                    }}
                  />
                  <button
                    onClick={() => handleAddNote(selectedQuote.id)}
                    style={{
                      padding: '10px 16px',
                      background: 'rgba(212, 175, 55, 0.1)',
                      border: '1px solid rgba(212, 175, 55, 0.3)',
                      color: '#D4AF37',
                      fontSize: '13px',
                      fontWeight: '600',
                      cursor: 'pointer'
                    }}
                  >
                    Add
                  </button>
                </div>
              </div>

              {/* Action Buttons */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
                <button
                  onClick={() => exportQuoteToPDF(selectedQuote)}
                  style={{
                    padding: '12px',
                    background: 'rgba(59, 130, 246, 0.1)',
                    border: '1px solid rgba(59, 130, 246, 0.3)',
                    color: '#3B82F6',
                    fontSize: '13px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px'
                  }}
                >
                  <Download size={14} />
                  Export PDF
                </button>
                <button
                  onClick={() => handleDelete(selectedQuote.id)}
                  disabled={currentAdmin?.role === 'analyst' || currentAdmin?.role === 'worker'}
                  style={{
                    padding: '12px',
                    background: (currentAdmin?.role === 'analyst' || currentAdmin?.role === 'worker') ? 'rgba(100, 100, 100, 0.1)' : 'rgba(255, 0, 0, 0.1)',
                    border: `1px solid ${(currentAdmin?.role === 'analyst' || currentAdmin?.role === 'worker') ? 'rgba(100, 100, 100, 0.2)' : 'rgba(255, 0, 0, 0.3)'}`,
                    color: (currentAdmin?.role === 'analyst' || currentAdmin?.role === 'worker') ? '#666' : '#FF6B6B',
                    fontSize: '13px',
                    fontWeight: '600',
                    cursor: (currentAdmin?.role === 'analyst' || currentAdmin?.role === 'worker') ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px'
                  }}
                >
                  <Trash2 size={14} />
                  Delete
                </button>
              </div>
              {(currentAdmin?.role === 'analyst' || currentAdmin?.role === 'worker') && (
                <div style={{ fontSize: '11px', color: '#FF6B6B', textAlign: 'center', marginBottom: '12px' }}>
                  ⚠ {currentAdmin?.role === 'analyst' ? 'Analysts' : 'Workers'} cannot delete quotes
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;
